import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {Image} from '@sportngin/se-commerce-sdk/models/clare';
import {SeFeCardModule} from 'se-fe-card';
import {SeFeCarouselModule} from 'se-fe-carousel';
import {SeFeFormFieldModule} from 'se-fe-form-field';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeModalComponent, SeFeModalModule} from 'se-fe-modal';
import {
  ProductImagesUpdate,
  UpdateProductDetail,
} from '../../../features/product/components/product-detail/product-detail.store';
import {Product} from '../../../models/clare';
import {ItemMediaModalComponent} from './item-media-modal/item-media-modal.component';

/**
 * Event interface for setting error state
 */
export interface SetErrorEvent {
  error: string | undefined;
}

@Component({
  selector: 'item-media-card',
  imports: [
    SeFeCarouselModule,
    SeFeCardModule,
    SeFeGridModule,
    SeFeFormFieldModule,
    ItemMediaModalComponent,
    SeFeModalModule,
  ],
  templateUrl: './item-media-card.component.html',
  styleUrls: ['./item-media-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class ItemMediaCardComponent {
  @ViewChild('mediaModal') mediaModal: SeFeModalComponent;

  @Input() product: Product;
  @Input() error: string;
  @Input() image: {src: string; alt: string};
  @Input() images: Image[] = []; // All product images for carousel. When set, the carousel is shown instead of a single image.
  /** When true (e.g. bundle detail), allow multiple images; when false (e.g. product detail), allow only one. */
  @Input() allowMultipleImages = false;

  @Output() updateProductImageAndDescription = new EventEmitter<UpdateProductDetail<ProductImagesUpdate>>();
  @Output() setError = new EventEmitter<SetErrorEvent>();

  imageDescriptionLabel = {label: 'Image description'};

  /** Carousel config: fixed width/height so the slide area never resizes and images don't cause jump or flash. */
  carouselConfig = {
    border: false,
    width: 240,
    height: 240,
    fixedSlides: 1,
    trackBy: 'index' as const, // se-fe-carousel built-in trackBy; avoids image re-load and flash when changing slides
  };

  /** Current carousel slide index (0-based), set from seFeSlideChange. Used to show the correct image description below the carousel. */
  currentSlideIndex = 0;

  get carouselContent(): {metadata: {total: number}; slides: Image[]} {
    const list = this.images?.length ? this.images : this.image ? [this.image] : [];
    return {
      metadata: {total: list.length},
      slides: [...list],
    };
  }

  /** The image (src + alt) for the currently visible carousel slide, or the single image when not using carousel. */
  get currentImageForDescription(): {src: string; alt: string} | undefined {
    const list = this.images?.length ? this.images : this.image ? [this.image] : [];
    const idx = this.images?.length ? this.currentSlideIndex : 0;
    return list[idx] ?? this.image;
  }

  constructor(private cdr: ChangeDetectorRef) {}

  onUpdateImage(event: UpdateProductDetail<ProductImagesUpdate>): void {
    this.updateProductImageAndDescription.emit(event);
    this.setError.emit({error: undefined});
  }

  onImageUploadFailed(): void {
    this.setError.emit({error: 'Image upload failed. Please try again.'});
  }

  onImageUploadSucceeded(): void {
    this.setError.emit({error: undefined});
  }

  /**
   * Handles carousel slide change so the Image description below stays in sync with the visible slide.
   */
  onCarouselSlideChange(event: {startIndex: number; endIndex: number}): void {
    this.currentSlideIndex = event.startIndex;
    this.cdr.markForCheck();
  }
}
